// src/components/capabilities-v2/CapabilitiesSection.js
// Clean implementation with working sticky and stacking cards

import { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, useReducedMotion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { pillarsData } from '../../data/capabilitiesData';
import StackingCard from './StackingCard';
import './CapabilitiesSection.css';

const CapabilitiesSection = () => {
  const sectionRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  // Calculate translateX distance based on viewport width
  const [translateX, setTranslateX] = useState(0);

  useEffect(() => {
    const calculateTranslate = () => {
      const vw = window.innerWidth;
      // Move text from center to left (~30% of viewport)
      setTranslateX(-(vw * 0.22));
    };
    calculateTranslate();
    window.addEventListener('resize', calculateTranslate);
    return () => window.removeEventListener('resize', calculateTranslate);
  }, []);

  // Scroll progress for the section
  // offset: ["start end", "end start"] means:
  // - Progress 0 = section START at viewport BOTTOM (entering)
  // - Progress ~14% = section TOP at viewport TOP (fully in view)
  // - Progress 100% = section END at viewport TOP (leaving)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  // ============================================
  // ANIMATIONS - Recalculated for new offset
  // With ["start end", "end start"] on 500vh section:
  // - 8% = section top at viewport center
  // - 14% = section top at viewport top
  // NO FADE OUT - sticky handles exit naturally
  // ============================================

  // Eyebrow pill - fades in when section top reaches viewport center (~8%)
  const pillOpacity = useTransform(scrollYProgress, [0.06, 0.10], [0, 1]);

  // Pill Y position - starts at center of viewport, moves up to top, stays there
  // At 6% scroll: pill at center (translateY = 50vh - 24px ≈ 450px)
  // At 14% scroll: pill at top (translateY = 0)
  // Stays at 0 until section ends
  const pillY = useTransform(scrollYProgress, [0.06, 0.14], [400, 0]);

  // Text content - fades in 14-20%, NO fade out
  const textOpacity = useTransform(scrollYProgress, [0.14, 0.20], [0, 1]);

  // Line-by-line text appearance (16-28% - TEXT AT CENTER)
  const line1Opacity = useTransform(scrollYProgress, [0.16, 0.20], [0, 1]);
  const line2Opacity = useTransform(scrollYProgress, [0.20, 0.24], [0, 1]);
  const line3Opacity = useTransform(scrollYProgress, [0.24, 0.28], [0, 1]);
  const subtitleOpacity = useTransform(scrollYProgress, [0.26, 0.32], [0, 1]);

  // Text moves left after all lines visible (38-48%)
  const textX = useTransform(scrollYProgress, [0.38, 0.48], [0, translateX]);

  // Cards container - appears 44-50%, NO fade out
  const cardsOpacity = useTransform(scrollYProgress, [0.44, 0.50], [0, 1]);

  // Smooth springs
  const smoothPillOpacity = useSpring(pillOpacity, { stiffness: 100, damping: 30 });
  const smoothPillY = useSpring(pillY, { stiffness: 100, damping: 30 });
  const smoothTextOpacity = useSpring(textOpacity, { stiffness: 100, damping: 30 });
  const smoothLine1 = useSpring(line1Opacity, { stiffness: 100, damping: 30 });
  const smoothLine2 = useSpring(line2Opacity, { stiffness: 100, damping: 30 });
  const smoothLine3 = useSpring(line3Opacity, { stiffness: 100, damping: 30 });
  const smoothSubtitle = useSpring(subtitleOpacity, { stiffness: 100, damping: 30 });
  const smoothTextX = useSpring(textX, { stiffness: 60, damping: 25 });
  const smoothCardsOpacity = useSpring(cardsOpacity, { stiffness: 60, damping: 25 });

  // Reduced motion fallback
  if (prefersReducedMotion) {
    return (
      <section className="cap-v2-section cap-v2-section--static">
        <div className="cap-v2-static">
          <div className="cap-v2-static__header">
            <span className="cap-v2-pill">
              <Sparkles size={14} />
              <span>Our Capabilities</span>
            </span>
            <h2>Three Pillars of <span className="gradient-text">Operational Excellence</span></h2>
            <p>We combine cutting-edge AI technology, advanced analytics, and custom software development to transform how enterprises operate.</p>
          </div>
          <div className="cap-v2-static__cards">
            {pillarsData.map((pillar) => (
              <div key={pillar.id} className="cap-v2-static__card">
                <h3>{pillar.title}</h3>
                <ul>
                  {pillar.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="cap-v2-section">
      {/* Background */}
      <div className="cap-v2-bg">
        <div className="cap-v2-bg__gradient" />
        <div className="cap-v2-bg__blob cap-v2-bg__blob--1" />
        <div className="cap-v2-bg__blob cap-v2-bg__blob--2" />
      </div>

      {/* PILL - Fixed position, Y controlled by scroll */}
      <motion.div
        className="cap-v2-pill"
        initial={{ opacity: 0 }}
        style={{
          opacity: smoothPillOpacity,
          y: smoothPillY
        }}
      >
        <Sparkles size={14} />
        <span>Our Capabilities</span>
      </motion.div>

      {/* STICKY WRAPPER - sticks at top: 0, 100vh tall */}
      <div className="cap-v2-sticky">
        {/* Main Content - Two columns */}
        <div className="cap-v2-content">

          {/* LEFT: Text (moves from center to left) */}
          <motion.div
            className="cap-v2-left"
            initial={{ opacity: 0 }}
            style={{
              opacity: smoothTextOpacity,
              x: smoothTextX,
              // CSS handles centering with transform: translate(-50%, -50%)
              // Framer x will animate from center to left
            }}
          >
            <h2 className="cap-v2-title">
              <motion.span style={{ opacity: smoothLine1, display: 'block' }}>
                Three Pillars of
              </motion.span>
              <motion.span className="gradient-text" style={{ opacity: smoothLine2, display: 'block' }}>
                Operational
              </motion.span>
              <motion.span className="gradient-text" style={{ opacity: smoothLine3, display: 'block' }}>
                Excellence
              </motion.span>
            </h2>
            <motion.p className="cap-v2-subtitle" style={{ opacity: smoothSubtitle }}>
              We combine cutting-edge AI technology, advanced analytics, and custom software development to transform how enterprises operate.
            </motion.p>
          </motion.div>

          {/* RIGHT: Stacking Cards */}
          <motion.div
            className="cap-v2-right"
            initial={{ opacity: 0 }}
            style={{ opacity: smoothCardsOpacity }}
          >
            <div className="cap-v2-cards">
              {pillarsData.map((pillar, index) => (
                <StackingCard
                  key={pillar.id}
                  pillar={pillar}
                  index={index}
                  scrollYProgress={scrollYProgress}
                  totalCards={pillarsData.length}
                />
              ))}
            </div>
          </motion.div>

        </div>
      </div>

      {/* SCROLL SPACER - Creates the scroll distance for sticky to work */}
      {/* Sticky needs content AFTER it to scroll through */}
      {/* Section is 500vh, sticky is 100vh, so spacer needs 400vh */}
      <div className="cap-v2-scroll-spacer" aria-hidden="true" />
    </section>
  );
};

export default CapabilitiesSection;
